"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { EyeIcon } from "@/components/queue/EyeIcon";
import Image from "next/image";
import { saveCadastroProfile } from "@/services/cadastroSession";
import {
  formatBirthDateInput,
  parseBirthDate,
} from "@/utils/birthDate";
import { normalizePersonName } from "@/utils/nameFormat";

interface FormState {
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  celular: string;
  email: string;
  senha: string;
}

interface TouchedState {
  nome: boolean;
  sobrenome: boolean;
  dataNascimento: boolean;
  celular: boolean;
  email: boolean;
  senha: boolean;
  acceptedTerms: boolean;
  acceptedInteractiveGamesTerms: boolean;
}

const INITIAL_FORM: FormState = {
  nome: "",
  sobrenome: "",
  dataNascimento: "",
  celular: "",
  email: "",
  senha: "",
};

const INITIAL_TOUCHED: TouchedState = {
  nome: false,
  sobrenome: false,
  dataNascimento: false,
  celular: false,
  email: false,
  senha: false,
  acceptedTerms: false,
  acceptedInteractiveGamesTerms: false,
};

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 2) return digits ? `(${digits}` : "";
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function QueueRegistrationForm() {
  const router = useRouter();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [touched, setTouched] = useState<TouchedState>(INITIAL_TOUCHED);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedInteractiveGamesTerms, setAcceptedInteractiveGamesTerms] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [stateCode, setStateCode] = useState("SP");

  useEffect(() => {
    const storedState = window.localStorage.getItem("state");

    if (storedState?.trim()) {
      setStateCode(storedState.trim().toUpperCase());
    }
  }, []);

  const isFormReady = useMemo(() => {
    return (
      form.nome.trim() &&
      form.sobrenome.trim() &&
      parseBirthDate(form.dataNascimento) &&
      form.celular.replace(/\D/g, "").length === 11 &&
      form.email.trim() &&
      form.senha &&
      acceptedTerms &&
      acceptedInteractiveGamesTerms
    );
  }, [acceptedInteractiveGamesTerms, acceptedTerms, form]);

  const fieldErrors = useMemo(() => {
    const errors: Partial<Record<keyof TouchedState, string>> = {};

    if (touched.nome && !form.nome.trim()) {
      errors.nome = "Informe seu nome.";
    }

    if (touched.sobrenome && !form.sobrenome.trim()) {
      errors.sobrenome = "Informe seu sobrenome.";
    }

    if (touched.dataNascimento) {
      if (!form.dataNascimento.trim()) {
        errors.dataNascimento = "Informe sua data de nascimento.";
      } else if (form.dataNascimento.trim().length < 10) {
        errors.dataNascimento = "Informe a data completa no formato DD/MM/AAAA.";
      } else if (!parseBirthDate(form.dataNascimento)) {
        errors.dataNascimento = "Informe uma data de nascimento válida.";
      }
    }

    if (touched.celular) {
      const phoneDigits = form.celular.replace(/\D/g, "");

      if (!phoneDigits) {
        errors.celular = "Informe seu celular.";
      } else if (phoneDigits.length !== 11) {
        errors.celular = "Informe um celular válido com DDD.";
      }
    }

    if (touched.email) {
      if (!form.email.trim()) {
        errors.email = "Informe seu e-mail.";
      } else if (!isValidEmail(form.email.trim())) {
        errors.email = "Informe um e-mail válido.";
      }
    }

    if (touched.senha) {
      if (!form.senha) {
        errors.senha = "Informe sua senha.";
      } else if (form.senha.length < 6) {
        errors.senha = "A senha deve ter pelo menos 6 caracteres.";
      }
    }

    if (touched.acceptedTerms && !acceptedTerms) {
      errors.acceptedTerms = "Você precisa aceitar os termos para continuar.";
    }

    if (
      touched.acceptedInteractiveGamesTerms &&
      !acceptedInteractiveGamesTerms
    ) {
      errors.acceptedInteractiveGamesTerms =
        "Você precisa concordar com os termos dos games interativos.";
    }

    return errors;
  }, [acceptedInteractiveGamesTerms, acceptedTerms, form, touched]);

  const hasVisibleErrors = Object.keys(fieldErrors).length > 0;

  function normalizeNameField(field: "nome" | "sobrenome") {
    return () => {
      setForm((current) => ({
        ...current,
        [field]: normalizePersonName(current[field]),
      }));
      setTouched((current) => ({ ...current, [field]: true }));
    };
  }

  function markTouched(field: keyof TouchedState) {
    return () => {
      setTouched((current) => ({ ...current, [field]: true }));
    };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/cadastro", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          telefone: form.celular,
          state: stateCode,
          acceptedTerms,
          acceptedInteractiveGamesTerms,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error ?? "Não foi possível concluir o cadastro.");
      }

      saveCadastroProfile(data.user);
      setShowSuccessModal(true);
      setForm(INITIAL_FORM);
      setTouched(INITIAL_TOUCHED);
      setAcceptedTerms(false);
      setAcceptedInteractiveGamesTerms(false);
      setShowPassword(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Não foi possível concluir o cadastro.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white w-full flex justify-center items-center">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-5 max-w-[400px] flex items-center justify-center flex-col mt-10"
      >
        <div className="text-center w-full">
          <h1 className="font-extrabold text-[#2033ef] text-[22px]">
            Cadastre-se para participar das experiências da Casa CazéTV
          </h1>
          <p className="text-[16px] text-black w-full max-w-[370px] lg:max-w-[570px] mt-4 text-wrap">
            Com o cadastro, você poderá participar das interações ao vivo e
            entrar na fila virtual para participar das experiências.
          </p>
        </div>
        <div className="mt-[22px] space-y-[10px] flex flex-col justify-center w-full">
          <input
            value={form.nome}
            onChange={(event) =>
              setForm((current) => ({ ...current, nome: event.target.value }))
            }
            onBlur={normalizeNameField("nome")}
            aria-invalid={Boolean(fieldErrors.nome)}
            placeholder="Nome:"
            className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
            autoComplete="given-name"
            style={{ textTransform: "none" }}
          />
          {fieldErrors.nome ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.nome}
            </p>
          ) : null}
          <input
            value={form.sobrenome}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                sobrenome: event.target.value,
              }))
            }
            onBlur={normalizeNameField("sobrenome")}
            aria-invalid={Boolean(fieldErrors.sobrenome)}
            placeholder="Sobrenome:"
            className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
            autoComplete="family-name"
            style={{ textTransform: "none" }}
          />
          {fieldErrors.sobrenome ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.sobrenome}
            </p>
          ) : null}
          <input
            value={form.dataNascimento}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                dataNascimento: formatBirthDateInput(event.target.value),
              }))
            }
            onBlur={markTouched("dataNascimento")}
            type="text"
            inputMode="numeric"
            maxLength={10}
            placeholder="Data de nascimento:"
            aria-invalid={Boolean(fieldErrors.dataNascimento)}
            className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
            autoComplete="bday"
            aria-label="Data de nascimento"
          />
          {fieldErrors.dataNascimento ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.dataNascimento}
            </p>
          ) : null}
          <input
            value={form.celular}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                celular: formatPhone(event.target.value),
              }))
            }
            onBlur={markTouched("celular")}
            placeholder="Celular:"
            inputMode="numeric"
            maxLength={15}
            aria-invalid={Boolean(fieldErrors.celular)}
            className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
            autoComplete="tel"
          />
          {fieldErrors.celular ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.celular}
            </p>
          ) : null}
          <input
            value={form.email}
            onChange={(event) =>
              setForm((current) => ({ ...current, email: event.target.value }))
            }
            onBlur={markTouched("email")}
            placeholder="E-mail:"
            type="email"
            aria-invalid={Boolean(fieldErrors.email)}
            className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
            autoComplete="email"
          />
          {fieldErrors.email ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.email}
            </p>
          ) : null}
          <div className="relative">
            <input
              value={form.senha}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  senha: event.target.value,
                }))
              }
              onBlur={markTouched("senha")}
              placeholder="Senha:"
              type={showPassword ? "text" : "password"}
              aria-invalid={Boolean(fieldErrors.senha)}
              className="h-[45px] w-full rounded-[3px] bg-[#e5e5e5] px-[9px] pr-9 text-[12px] text-black outline-none placeholder:text-[#7a7a7a]"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-[#4a4a4a]"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          {fieldErrors.senha ? (
            <p className="-mt-1 text-[11px] leading-snug text-red-600">
              {fieldErrors.senha}
            </p>
          ) : null}
        </div>

        <div className="mt-[17px] flex items-start gap-[9px]">
          <input
            id="queue-registration-terms"
            type="checkbox"
            checked={acceptedTerms}
            onChange={(event) => {
              setAcceptedTerms(event.target.checked);
              setTouched((current) => ({ ...current, acceptedTerms: true }));
            }}
            className="mt-[2px] h-[12px] w-[12px] shrink-0 cursor-pointer appearance-none rounded-full border border-black bg-white p-[2px] checked:bg-[radial-gradient(circle,#000_0_45%,transparent_47%)]"
          />
          <label
            htmlFor="queue-registration-terms"
            className="text-[14px] leading-[1.2] text-black"
          >
            <span className="block">
              Ao continuar, você aceita os{" "}
              <Link
                href="/termos-de-uso"
                className="underline underline-offset-3"
                target="_blank"
              >
                termos de Uso
              </Link>{" "}
              e{" "}
              <Link
                href="/politica-de-privacidade"
                className="underline underline-offset-3"
                target="_blank"
              >
                Política de privacidade
              </Link>
              .
            </span>
          </label>
        </div>
        {fieldErrors.acceptedTerms ? (
          <p className="mt-1 w-full text-[11px] leading-snug text-red-600">
            {fieldErrors.acceptedTerms}
          </p>
        ) : null}

        <div className="mt-2 flex items-start gap-[9px]">
          <input
            id="queue-registration-interactive-games-terms"
            type="radio"
            name="queue-registration-interactive-games-terms"
            checked={acceptedInteractiveGamesTerms}
            onClick={() => {
              setAcceptedInteractiveGamesTerms((current) => !current);
              setTouched((current) => ({
                ...current,
                acceptedInteractiveGamesTerms: true,
              }));
            }}
            onChange={() => undefined}
            className="mt-[2px] h-[12px] w-[12px] shrink-0 cursor-pointer appearance-none rounded-full border border-black bg-white p-[2px] checked:bg-[radial-gradient(circle,#000_0_45%,transparent_47%)]"
          />
          <label
            htmlFor="queue-registration-interactive-games-terms"
            className="text-[14px] leading-[1.2] text-black"
          >
            <span className="mt-1 block">
              Ao participar dos games interativos, você concorda com os{" "}
              <Link
                href="/termos/termos.pdf"
                className="underline underline-offset-3"
                target="_blank"
              >
                termos de Uso
              </Link>
              .
            </span>
          </label>
        </div>
        {fieldErrors.acceptedInteractiveGamesTerms ? (
          <p className="mt-1 w-full text-[11px] leading-snug text-red-600">
            {fieldErrors.acceptedInteractiveGamesTerms}
          </p>
        ) : null}

        {error ? (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] leading-snug text-red-600">
            {error}
          </p>
        ) : null}

        {!isFormReady && hasVisibleErrors ? (
          <p className="mt-3 text-center text-[11px] leading-snug text-red-600">
            Revise os campos destacados para continuar.
          </p>
        ) : null}

        <button
          type="submit"
          disabled={loading || !isFormReady}
          className="mx-auto mt-7 block w-[230px] cursor-pointer transition-transform duration-180 hover:not-disabled:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-45"
          aria-label={loading ? "Concluindo cadastro" : "Entrar"}
        >
          {/*<QueueEnterButtonImage className="h-auto w-full" />*/}
          <Image
            src="/icons/cadastrar.svg"
            height={30}
            width={290}
            alt="Entrar"
          />
        </button>

        <p className="mt-5 text-center text-[14px] leading-[1.25] text-black">
          Já tem cadastro?
          <Link href="/entrar" className="block underline">
            Clique aqui
          </Link>
        </p>
      </form>

      {showSuccessModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="relative w-full max-w-[300px] rounded-[14px] bg-[#fec822] px-5 pb-7 pt-9">
            <button
              type="button"
              onClick={() => {
                setShowSuccessModal(false);
                router.push("/home");
              }}
              className="absolute -right-[10px] -top-[12px] flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#2f43ef] text-white"
              aria-label="Fechar modal de sucesso"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.7"
                className="h-5 w-5"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            <h2 className="text-center text-[18px] font-extrabold uppercase leading-none text-[#2033ef]">
              Cadastro concluído
            </h2>
            <p className="mt-5 text-center text-[14px] leading-[1.15] text-black">
              Agora você já pode entrar na fila virtual de uma experiência e
              também participar dos jogos interativos que rolam ao vivo por
              aqui.
            </p>
            <button
              type="button"
              onClick={() => router.push("/home")}
              className="mt-6 w-full rounded-full bg-[#2033ef] px-4 py-3 text-[14px] font-extrabold uppercase text-white"
            >
              Ir para home
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
